/* eslint-disable no-param-reassign */
import React, { ChangeEvent, KeyboardEvent, ReactElement, RefObject, Dispatch, SetStateAction, FocusEvent } from 'react'
import classnames from 'classnames'
import { ICreatableSelectFieldOption } from '../../ICreatableSelectFieldOption'
import style from './Input.module.css'

type InputProps<Option> = {
  valueChange(currentValue: string): void
  selectItem(option: Option): void
  activeItemChange: Dispatch<SetStateAction<number>>
  isOpenChange: Dispatch<SetStateAction<boolean>>
  onFocusHandler(e: FocusEvent<HTMLInputElement, Element>): void
  onBlurHandler(e: FocusEvent<HTMLInputElement, Element>): void
  setTouch: (isTouch: boolean) => void
  filtredOptions: ICreatableSelectFieldOption<Option>[]
  countOfListItems: number
  listItemHeight: number
  activeItem: number
  value: string
  placeholder: string
  readonly?: boolean
  disabled?: boolean
  className?: string
  isValidationError: boolean
  inputRef: RefObject<HTMLInputElement>
  listRef: RefObject<HTMLUListElement>
}

export const Input = <Option,>({
  isOpenChange,
  valueChange,
  selectItem,
  activeItemChange,
  setTouch,
  onFocusHandler,
  onBlurHandler,
  className,
  listItemHeight,
  filtredOptions,
  activeItem,
  value,
  placeholder,
  readonly = false,
  disabled = false,
  isValidationError,
  countOfListItems,
  inputRef,
  listRef,
}: InputProps<Option>): ReactElement => {
  const scrollList = (selectedItem: number) => {
    if (listRef.current) {
      const { scrollTop } = listRef.current
      const listHeight = listItemHeight * countOfListItems + scrollTop
      const selectedItemTop = listItemHeight * selectedItem
      const selectedItemBottom = listItemHeight * (selectedItem + 1)

      if (selectedItemBottom > listHeight) {
        listRef.current.scrollTop = (selectedItem - countOfListItems + 1) * listItemHeight
      }

      if (selectedItemTop < scrollTop) {
        listRef.current.scrollTop = selectedItem * listItemHeight
      }
    }
  }
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { target, key } = e

    if (key === 'ArrowUp') {
      activeItemChange((activeItemCurrent) => {
        const newActiveItemCurrent = !activeItemCurrent ? filtredOptions.length - 1 : activeItemCurrent - 1

        scrollList(newActiveItemCurrent)

        return newActiveItemCurrent
      })
    }

    if (key === 'ArrowDown') {
      activeItemChange((activeItemCurrent) => {
        const newActiveItemCurrent = activeItemCurrent === filtredOptions.length - 1 ? 0 : activeItemCurrent + 1

        scrollList(newActiveItemCurrent)

        return newActiveItemCurrent
      })
    }

    if (key === 'Enter' || key === 'Space') {
      e.preventDefault()

      selectItem(filtredOptions[activeItem].option as Option)
      activeItemChange(0)
      scrollList(0)
      isOpenChange(false)

      setTimeout(() => (target as HTMLInputElement).blur(), 0)
    }
  }

  const onChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    valueChange(target.value)
    if (target.value.length > 2) {
      isOpenChange(true)
    } else {
      isOpenChange(false)
    }
    activeItemChange(0)
  }

  const onBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    let { relatedTarget } = e

    if (relatedTarget === null) {
      relatedTarget = document.activeElement
    }

    if (!(relatedTarget as HTMLElement)?.dataset?.creatableselectfield) {
      isOpenChange(false)
      setTouch(true)
    }

    if (onBlurHandler) {
      onBlurHandler(e)
    }
  }

  const onFocus = (e: FocusEvent<HTMLInputElement, Element>) => {
    if (onFocusHandler) {
      onFocusHandler(e)
    }
  }

  const classNames = classnames(style.index, className, {
    [style.errorField]: isValidationError,
  })

  return (
    <input
      readOnly={readonly}
      disabled={disabled}
      ref={inputRef}
      autoComplete="off"
      className={classNames}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      value={value}
      placeholder={placeholder}
    />
  )
}
